import { FC, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import { WordlistsApi } from 'src/api';
import { SessionSourceType } from 'src/api/learningSessions/types';
import { getActiveWordlistId } from 'src/utils/wordlists/getActiveWordlistId';
import { useTrackTooEasyForYou } from 'src/apps/LearningSession/Common/ExploreDifferentVocabModal/useTrackTooEasyForYou';
import { OptionsDialog } from '../OptionsDialog';
import { BLOB_TRACKING_DATA } from '../tracking';
import * as S from './styles';

const msgs = defineMessages({
  title: {
    id: 'blob_session_too_easy_modal_title',
    defaultMessage: 'This word list might be too easy',
  },
  description: {
    id: 'blob_session_too_easy_modal_description',
    defaultMessage:
      'You’ve said “I already know this” twice in a row. Want to switch to something new?',
  },
  changeWordlist: {
    id: 'blob_session_too_easy_modal_change_wordlist',
    defaultMessage: 'Change word list',
  },
  changeWordlistDescription: {
    id: 'blob_session_too_easy_modal_change_wordlist_description',
    defaultMessage: 'Pick a different word list to learn next',
  },
  keepGoing: {
    id: 'blob_session_too_easy_modal_keep_going',
    defaultMessage: 'Keep going with this word list',
  },
  keepGoingDescription: {
    id: 'blob_session_too_easy_modal_keep_going_description',
    defaultMessage: 'Continue with “{wordlistName}”',
  },
});

/** Path blobs and classic scenarios already expose their title on the session source. */
const usesSessionSourceName = (sessionSourceType: SessionSourceType): boolean =>
  sessionSourceType === 'user_path_blob' || sessionSourceType === 'scenario';

type TooEasyModalProps = {
  isOpen: boolean;
  languagePairId: number;
  sessionSourceType: SessionSourceType;
  sessionSourceName: string | null;
  onKeepGoing: () => void;
  onChangeWordlist: () => void;
};

export const TooEasyModal: FC<TooEasyModalProps> = ({
  isOpen,
  languagePairId,
  sessionSourceType,
  sessionSourceName,
  onKeepGoing,
  onChangeWordlist,
}) => {
  const intl = useIntl();
  const trackTooEasyForYou = useTrackTooEasyForYou(languagePairId, BLOB_TRACKING_DATA);
  const activeWordlistId = getActiveWordlistId();
  const shouldFetchWordlist = !usesSessionSourceName(sessionSourceType);

  const { data: wordlistData } = useQuery({
    queryKey: ['getWordlist', languagePairId, activeWordlistId],
    queryFn: () => WordlistsApi.getWordlist(languagePairId, activeWordlistId as string),
    enabled: isOpen && shouldFetchWordlist && !!activeWordlistId,
  });
  const keepGoingName = shouldFetchWordlist ? wordlistData?.wordlist.title : sessionSourceName;

  useEffect(() => {
    if (isOpen) trackTooEasyForYou('screen_viewed');
  }, [isOpen, trackTooEasyForYou]);

  const handleKeepGoing = () => {
    trackTooEasyForYou('too_easy_for_you_dismissed');
    onKeepGoing();
  };

  const handleChangeWordlist = () => {
    trackTooEasyForYou('too_easy_for_you_explore_more');
    onChangeWordlist();
  };

  return (
    <OptionsDialog
      isOpen={isOpen}
      onClose={handleKeepGoing}
      contentLabel={intl.formatMessage(msgs.title)}
      data-testid="blob-too-easy-modal"
    >
      <S.Header>
        <S.Title>{intl.formatMessage(msgs.title)}</S.Title>
        <S.Description>{intl.formatMessage(msgs.description)}</S.Description>
      </S.Header>
      <S.OptionsList>
        <S.OptionButton
          type="button"
          $primary
          onClick={handleChangeWordlist}
          data-testid="blob-change-wordlist"
        >
          <S.OptionIcon name="wordlists" />
          <S.OptionText>
            <S.OptionTitle>{intl.formatMessage(msgs.changeWordlist)}</S.OptionTitle>
            <S.OptionDescription>
              {intl.formatMessage(msgs.changeWordlistDescription)}
            </S.OptionDescription>
          </S.OptionText>
        </S.OptionButton>
        <S.OptionButton type="button" onClick={handleKeepGoing} data-testid="blob-keep-going">
          <S.OptionIcon name="chevronRight" />
          <S.OptionText>
            <S.OptionTitle>{intl.formatMessage(msgs.keepGoing)}</S.OptionTitle>
            {keepGoingName && (
              <S.OptionDescription>
                {intl.formatMessage(msgs.keepGoingDescription, { wordlistName: keepGoingName })}
              </S.OptionDescription>
            )}
          </S.OptionText>
        </S.OptionButton>
      </S.OptionsList>
    </OptionsDialog>
  );
};
